import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { TripPODResponse } from "../../types/api";
import { useTranslation } from "react-i18next";
import { useTripDocument } from "../../context/TripDocumentContext";
import {
  PKLPayloadFechtMetaData,
  PODPayloadFechtMetaData,
} from "../tripDetails/docPayloads";
import { useTripContext } from "../../context/tripsContext";
import { theme } from "../../utils/theme/theme";
import { useNavigate, useParams } from "react-router-dom";
import {
  AddRounded,
  DocumentScannerRounded,
  DownloadRounded,
  DownloadSharp,
  ExpandMoreRounded,
  UploadSharp,
} from "@mui/icons-material";
import { RelatedLoadsCardsDocument } from "../tripDetails/components/RelatedLoadsCardsDocuments";
import AgGridDocuments from "./AgGridDocuments";
import {
  cardDocument,
  documentPage,
  documentsCard,
  loadsCard,
} from "../tripDetails/style";
import SidebarAddDocument from "../tripDetails/components/timelineAddDocument/SidebarAddDocument";
import SidebarAddDocumentGrid from "../tripDetails/components/timelineAddDocument/SidebarAddDocumentGrid";
import TripDocumentsHeader from "./components/TripDocumentsHeader";
import ViewModeToggle from "./components/ViewModeToggle";
import { useAuth } from "../../utils/hooks/useAuth";
import { useDaysDifference } from "../../utils/hooks/useDaysDifference";
import documentsIcon from "../../assets/images/icons/contract_doc.svg";
import documentsIcon2 from "../../assets/images/icons/documentsGridIcon.svg";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
const TripDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { tripDetails, stops, fetchStops, fetchTripData } = useTripContext();
  const {
    fetchLatestPODDoc,
    fetchLatestPKLDoc,
    isDocumentLoading,
    isPackingListLoading,
    getTermsAndConditions,
  } = useTripDocument();
  const [filteredDocuments, setFilteredDocuments] = useState<TripPODResponse[]>(
    []
  );
  const [packingListDocuments, setPackingListDocuments] = useState<
    TripPODResponse[]
  >([]);
  const [isDocumentsReady, setIsDocumentsReady] = useState(false);
  const [isPackingListReady, setIsPackingListReady] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const [enrichedDocuments, setEnrichedDocuments] = useState<TripPODResponse[]>(
    []
  );
  const [enrichedPackingListDocuments, setEnrichedPackingListDocuments] =
    useState<TripPODResponse[]>([]);
  const [viewMode, setViewMode] = useState<"cards" | "grid">("cards");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isUpdatingDocuments, setIsUpdatingDocuments] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [currentLoadingTmsLocation, setCurrentLoadingTmsLocation] =
    useState<string>("");

  // Stati per la sidebar della grid
  const [isGridSidebarOpen, setIsGridSidebarOpen] = useState(false);
  const [gridDocumentData, setGridDocumentData] = useState<any>(null);

  const [terms, setTerms] = useState<{
    duvri: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
    generalTerms: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
    sop: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
  } | null>(null);
  const [isTermsLoading, setIsTermsLoading] = useState(false);

  // Determina quando tutti i dati sono pronti per la ricerca
  const isAllDataReady = useMemo(() => {
    return (
      !isInitialLoading && // Non stiamo più caricando i dati principali
      tripDetails && // I dettagli del viaggio sono caricati
      tripDetails.tmsObjectNumber && // Abbiamo l'ID del viaggio
      stops &&
      stops.length > 0 && // Le tappe sono caricate
      !isTermsLoading // Non stiamo più caricando i termini
    );
  }, [isInitialLoading, tripDetails, stops, isTermsLoading]);

  console.log("[DEBUG] TripDocument - Data ready status:", {
    isInitialLoading,
    hasTripDetails: !!tripDetails,
    hasTripId: !!tripDetails?.tmsObjectNumber,
    hasStops: stops && stops.length > 0,
    isTermsLoading,
    isAllDataReady,
  });

  const { user } = useAuth();

  // Hook per calcolare la differenza in giorni
  const {
    daysDifference,
    isValid,
    error: daysError,
  } = useDaysDifference(tripDetails?.tripCreationDate);

  const createDownloadLink = (base64: string | null, fileName: string) => {
    if (!base64) {
      console.error("Base64 string is null or undefined.");
      return null;
    }

    try {
      // Rimuovi prefissi se esistono, ad esempio: "data:application/pdf;base64,"
      const cleanBase64 = base64.includes(",") ? base64.split(",")[1] : base64;

      // Decodifica la stringa Base64 in un array di byte
      const binary = atob(cleanBase64);
      const binaryLength = binary.length;
      const bytes = new Uint8Array(binaryLength);
      for (let i = 0; i < binaryLength; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      // Crea un Blob con il tipo MIME per PDF
      const blob = new Blob([bytes], { type: "application/pdf" });

      // Genera un URL per il download
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error while creating download link:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchTerms = async () => {
      setIsTermsLoading(true);
      try {
        const response = await getTermsAndConditions(
          user ? user.accountId : "",
          user ? user.contactId : "",
          "EN",
          true
        );
        setTerms(response);
      } catch (error) {
        console.error("Errore:", error);
      } finally {
        setIsTermsLoading(false);
      }
    };

    fetchTerms();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([fetchTripData(Number(id)), fetchStops(Number(id))]);
      } catch (error) {
        console.error("Error fetching trip data:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    setHasFetched(false);
  }, [id, isAllDataReady]);

  useEffect(() => {
    if (
      isAllDataReady && // Aspetta che tutti i dati siano pronti
      !hasFetched
    ) {
      console.log(
        "[DEBUG] TripDocument - All data ready, starting document search"
      );
      fetchPackingListDocuments();
      fetchDocuments();
      setHasFetched(true);

      // Log del risultato del calcolo giorni
      console.log("[DEBUG] Days difference from hook:", {
        daysDifference,
        isValid,
        error: daysError,
      });
    }
  }, [isAllDataReady, hasFetched, daysDifference, isValid, daysError]);

  const fetchDocuments = async () => {
    setError(null);
    setIsDocumentsReady(false);
    setIsUpdatingDocuments(true);
    try {
      const currentTmsObjectNumber = tripDetails?.tmsObjectNumber || "";
      if (!currentTmsObjectNumber) {
        setError("Trip details non disponibile.");
        return;
      }

      const dynamicPayload = {
        ...PODPayloadFechtMetaData,
        searchFilterDtoList: PODPayloadFechtMetaData.searchFilterDtoList.map(
          (filter) => ({
            ...filter,
            fields: [
              ...filter.fields.filter(
                (field) =>
                  field.name !== "TESTO227_131" &&
                  field.name !== "Creation_Date_NrGiorni"
              ),
              {
                operator: 1,
                valore2: null,
                multiple: currentTmsObjectNumber,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "FieldBaseForSearchStringDto",
                groupId: 2,
                fieldType: 2,
                additionalFieldType: 0,
                defaultOperator: 1,
                tableName: "DM_PROFILE",
                binderFieldId: 0,
                description: "OPE - Codice Viaggio Consegna",
                order: 23,
                dataSource: null,
                required: false,
                formula: "",
                locked: false,
                comboGruppiId: null,
                dependencyFields: [],
                associations: null,
                isAdditional: true,
                visible: true,
                predefinedProfileFormula: null,
                dataSourceDiscriminator: null,
                and: null,
              },
              {
                operator: 3,
                valore1: isValid ? daysDifference : 180, // Usa il valore calcolato dinamicamente o fallback a 180
                valore2: null,
                multiple: null,
                name: "Creation_Date_NrGiorni",
                description: "Data reg. indietro di:",
                className: "FieldBaseForSearchIntDto",
              },
            ],
          })
        ),
      };

      console.log("[DEBUG] TripDocument - Dynamic payload:", {
        currentTmsObjectNumber,
        daysDifference,
        isValid,
        payload: dynamicPayload,
      });

      const response = await fetchLatestPODDoc(dynamicPayload);

      let groupedDocuments: { [key: string]: TripPODResponse } = {};

      if (response && Array.isArray(response)) {
        groupedDocuments = response.reduce<{
          [key: string]: TripPODResponse;
        }>((acc, doc) => {
          if (
            !doc.TESTO246_136 ||
            !doc.TESTO227_131 ||
            doc.DOCNUMBER === null
          ) {
            return acc; // Ignora i documenti con dati mancanti
          }

          if (doc.TESTO227_131 === currentTmsObjectNumber) {
            const groupKey = doc.TESTO246_136;

            // Se acc[groupKey] non esiste, assegna direttamente il documento
            if (!acc[groupKey]) {
              acc[groupKey] = doc;
            } else {
              // Confronta il DOCNUMBER solo se acc[groupKey] esiste
              const existingDoc = acc[groupKey];
              if (
                existingDoc.DOCNUMBER !== null &&
                existingDoc.DOCNUMBER < doc.DOCNUMBER
              ) {
                acc[groupKey] = doc; // Aggiorna il documento nel gruppo se ha un DOCNUMBER più alto
              }
            }
          }

          return acc;
        }, {});
      } else {
        console.warn(
          "La risposta API è null o non valida, continuando con array vuoto..."
        );
      }

      setFilteredDocuments(Object.values(groupedDocuments));
      const enrichedDocs = Object.values(groupedDocuments).map((doc) => {
        // Estrai il groupKey dal documento
        const groupKey = doc.TESTO246_136?.split("_")[0];
        const groupKey2 = doc.TESTO246_136?.split("_")[1];

        // Trova lo stop associato al documento corrente
        const currentStop = stops.find(
          (stop) => stop.tmsLocation === groupKey2
        );
        const currentUnloadingPointIndex = stops.findIndex(
          (stop) => stop.tmsLocation === groupKey2
        );
        const stop = stops.find((stop) => stop.tmsLocation === groupKey2);
        const stop2 = stops.find((stop) => stop.tmsLocation === groupKey);
        const _loadingPoint = stop2?.invPartyName || "N/A";
        const currentLoadingPointIndex = stops.findIndex(
          (stop) => stop.tmsLocation === groupKey
        );

        // Trova il prossimo stop per l'unloading point
        const _unloadingPoint = currentStop?.invPartyName || "-";

        // Trova il relatedLoad corrispondente per ottenere transportOrderTMSKey
        const relatedLoad = currentStop?.relatedLoads?.find(
          (load) => load.tmsLocation === groupKey
        );

        return {
          ...doc,
          loadingPoint: _loadingPoint,
          unloadingPoint: _unloadingPoint,
          currentUnloadingPointIndex: currentUnloadingPointIndex,
          currentLoadingPointIndex: currentLoadingPointIndex,
          // Aggiungi i dati necessari per la grid
          loadingTmsLocation: groupKey,
          unloadingTmsLocation: groupKey2,
          transportOrderTMSKey:
            (relatedLoad as any)?.transportOrderTMSKey || "N/A",
          involvedPartyName: relatedLoad?.involvedPartyName || "N/A",
          invPartyName: currentStop?.invPartyName || "N/A",
          tmsObjectNumber: tripDetails?.tmsObjectNumber || "N/A",
        };
      });

      const additionalDocs: typeof enrichedDocs = [];
      stops.forEach((stop) => {
        const stopLoadsCount = stop.relatedLoads?.length || 0;

        // Per ogni related load, controlla se esiste già un documento per quella specifica coppia loading-unloading
        stop.relatedLoads?.forEach((relatedLoad, loadIndex) => {
          if (relatedLoad.podSectionRequired) {
            const expectedDocumentKey = `${relatedLoad.tmsLocation}_${stop.tmsLocation}`;

            // Controlla se esiste già un documento per questa specifica coppia
            const existingDoc = enrichedDocs.find(
              (doc) => doc.TESTO246_136 === expectedDocumentKey
            );

            // Se non esiste un documento per questa coppia, crea un documento aggiuntivo
            if (!existingDoc) {
              // Determina l'indice corretto dello stop come unloading point
              const currentUnloadingPointIndex = stops.findIndex(
                (s) => s.tmsLocation === stop.tmsLocation
              );

              // Determina l'indice corretto dello stop come loading point
              const currentLoadingPointIndex = stops.findIndex(
                (s) => s.tmsLocation === relatedLoad?.tmsLocation
              );

              const additionalDoc = {
                NOTE: null,
                REVISIONE: 0,
                AUTOREDESCRIPTION: "-",
                CLASSEDOC: "OPE.POD",
                COMBO221_131: "POD",
                COMBO222_131: "VLM;MOBILE",
                CREATION_DATE: "-",
                DATA224_131: "-",
                DOCNAME: `-`,
                DOCNUMBER: 0, // Numero casuale per esempio
                FORMATO: "pdf",
                MULTI336_142: "",
                NUMERIC60_8: 1,
                ORIGINALE: "-",
                STATO: "DEFAULT",
                TESTO223_131: "-",
                TESTO227_131: currentTmsObjectNumber || "N/A",
                TESTO246_136: expectedDocumentKey,
                TESTO265_131: (relatedLoad as any)?.transportOrderTMSKey || "N/A",
                WFVERSIONSTATE: 8,
                WORKFLOW: 2,
                loadingPoint: relatedLoad?.involvedPartyName || "N/A",
                unloadingPoint: stop.invPartyName || "N/A",
                currentUnloadingPointIndex: currentUnloadingPointIndex,
                currentLoadingPointIndex: currentLoadingPointIndex,
                // Aggiungi i dati necessari per la grid
                loadingTmsLocation: relatedLoad?.tmsLocation || "N/A",
                unloadingTmsLocation: stop.tmsLocation,
                transportOrderTMSKey:
                  (relatedLoad as any)?.transportOrderTMSKey || "N/A",
                involvedPartyName: relatedLoad?.involvedPartyName || "N/A",
                invPartyName: stop.invPartyName || "N/A",
                tmsObjectNumber: currentTmsObjectNumber || "N/A",
              };

              additionalDocs.push(additionalDoc);
            }
          }
        });
      });

      // Log unico per il debug della generazione documenti
      console.log("[DEBUG] Document Generation Analysis:", {
        // Stops analysis
        totalStops: stops.length,
        deliveryStops: stops
          .filter((s) =>
            s.relatedActivities?.some(
              (activity: any) => activity.activityType === "DELIVERY"
            )
          )
          .map((s) => ({
            tmsLocation: s.tmsLocation,
            invPartyName: s.invPartyName,
            relatedLoadsCount: s.relatedLoads?.length || 0,
            relatedLoads:
              s.relatedLoads?.map((rl) => ({
                tmsLocation: rl.tmsLocation,
                involvedPartyName: rl.involvedPartyName,
                transportOrderTMSKey: (rl as any).transportOrderTMSKey,
              })) || [],
          })),

        // Existing documents analysis
        enrichedDocsCount: enrichedDocs.length,
        enrichedDocs: enrichedDocs.map((doc) => ({
          TESTO246_136: doc.TESTO246_136,
          loadingPoint: doc.loadingPoint,
          unloadingPoint: doc.unloadingPoint,
          DOCNUMBER: doc.DOCNUMBER,
        })),

        // Additional documents analysis
        additionalDocsCount: additionalDocs.length,
        additionalDocs: additionalDocs.map((doc) => ({
          TESTO246_136: doc.TESTO246_136,
          loadingPoint: doc.loadingPoint,
          unloadingPoint: doc.unloadingPoint,
          loadingTmsLocation: doc.loadingTmsLocation,
          unloadingTmsLocation: doc.unloadingTmsLocation,
        })),

        // Final result
        finalDocsCount: enrichedDocs.length + additionalDocs.length,

        // Specific check for DLO7-DLO8 pair
        dlo7Dlo8Pair: additionalDocs.find(
          (doc) =>
            doc.loadingTmsLocation ===
              "T2BaseMittentiDestinatari>MI>11>195049" &&
            doc.unloadingTmsLocation ===
              "T2BaseMittentiDestinatari>MI>11>202326"
        ),

        // Debug: Check specific pairs that should exist
        expectedPairs: stops
          .filter((s) =>
            s.relatedActivities?.some(
              (activity: any) => activity.activityType === "DELIVERY"
            )
          )
          .flatMap(
            (s) =>
              s.relatedLoads?.map((rl) => ({
                pair: `${rl.tmsLocation}_${s.tmsLocation}`,
                loadingPoint: rl.involvedPartyName,
                unloadingPoint: s.invPartyName,
                hasExistingDoc: enrichedDocs.some(
                  (doc) =>
                    doc.TESTO246_136 === `${rl.tmsLocation}_${s.tmsLocation}`
                ),
              })) || []
          ),
      });

      const finalEnrichedDocs = [...enrichedDocs, ...additionalDocs];
      setEnrichedDocuments(finalEnrichedDocs);
      setIsDocumentsReady(true);
    } catch (err: any) {
      setError(err.message || "Errore durante il recupero dei dati.");
      setIsDocumentsReady(false);
    } finally {
      setIsUpdatingDocuments(false);
      // Assicuriamoci che isDocumentsReady sia true anche in caso di errori
      setIsDocumentsReady(true);
    }
  };
  const [action, setAction] = useState<boolean>(false);
  const fetchPackingListDocuments = async () => {
    try {
      const currentTmsObjectNumber = tripDetails?.tmsObjectNumber || "";
      if (!currentTmsObjectNumber) {
        setError("Trip details non disponibile.");
        return;
      }

      const dynamicPayload = {
        ...PKLPayloadFechtMetaData,
        searchFilterDtoList: PKLPayloadFechtMetaData.searchFilterDtoList.map(
          (filter) => ({
            ...filter,
            fields: [
              ...filter.fields.filter(
                (field) =>
                  ![
                    "TESTO227_131",
                    "DocumentType",
                    "Creation_Date_NrGiorni",
                  ].includes(field.name)
              ),
              {
                operator: 1,
                valore2: null,
                multiple: currentTmsObjectNumber,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "FieldBaseForSearchStringDto",
                groupId: 2,
                fieldType: 2,
                additionalFieldType: 0,
                defaultOperator: 1,
                tableName: "DM_PROFILE",
                binderFieldId: 0,
                description: "OPE - Codice Viaggio Consegna",
                order: 23,
                dataSource: null,
                required: false,
                formula: "",
                locked: false,
                comboGruppiId: null,
                dependencyFields: [],
                associations: null,
                isAdditional: true,
                visible: true,
                predefinedProfileFormula: null,
                dataSourceDiscriminator: null,
                and: null,
              },
              {
                operator: 3,
                valore1: {
                  id: 357,
                  type2: 357,
                  type3: 0,
                  documentType: 67,
                  idParent: 67,
                  key: "OPE.ALLGEN",
                },
                valore2: null,
                multiple: null,
                name: "DocumentType",
                className: "FieldBaseForSearchDocumentTypeDto",
              },
              {
                operator: 3,
                valore1: isValid ? daysDifference : 180, // Usa il valore calcolato dinamicamente o fallback a 180
                valore2: null,
                multiple: null,
                name: "Creation_Date_NrGiorni",
                description: "Data reg. indietro di:",
                className: "FieldBaseForSearchIntDto",
              },
            ],
          })
        ),
      };

      console.log("[DEBUG] TripDocument - PKL Dynamic payload:", {
        currentTmsObjectNumber,
        daysDifference,
        isValid,
        payload: dynamicPayload,
      });

      const response = await fetchLatestPKLDoc(dynamicPayload);

      // Gestiamo sia il caso di array vuoto che di risposta non valida
      if (!response) {
        console.warn(
          "La risposta API per packing list è null, continuando con array vuoto..."
        );
        setPackingListDocuments([]);
        setEnrichedPackingListDocuments([]);
        setIsPackingListReady(true);
        return;
      }

      if (!Array.isArray(response)) {
        console.warn(
          "La risposta API per packing list non è un array, continuando con array vuoto..."
        );
        setPackingListDocuments([]);
        setEnrichedPackingListDocuments([]);
        setIsPackingListReady(true);
        return;
      }

      setPackingListDocuments(response);
      const enrichedPackingDocs = Object.values(response).map((doc) => {
        return {
          ...doc,
          loadingPoint: "-",
          unloadingPoint: "-",
        };
      });

      setEnrichedPackingListDocuments(enrichedPackingDocs);
      setIsPackingListReady(true);
    } catch (err: any) {
      setError(err.message || "Errore durante il recupero dei dati.");
      // Anche in caso di errore, impostiamo i documenti come array vuoto e ready
      setPackingListDocuments([]);
      setEnrichedPackingListDocuments([]);
      setIsPackingListReady(true);
    }
  };

  // Modifica la funzione openSidebar per accettare 3 parametri
  const openSidebar = (
    deliveryIndex: number,
    loadingTmsLocation: string,
    action: boolean
  ) => {
    console.log("[DEBUG] openSidebar called with:", {
      deliveryIndex,
      loadingTmsLocation,
      action,
    });
    setAction(action);
    setCurrentIndex(deliveryIndex);
    setCurrentLoadingTmsLocation(loadingTmsLocation); // nuovo stato
    setIsSidebarOpen(true);
  };

  // Nuova funzione per aprire la sidebar della grid
  const openGridSidebar = (documentData: any, action: boolean) => {
    console.log("[DEBUG] openGridSidebar called with:", {
      documentData,
      action,
    });
    setAction(action);
    setGridDocumentData(documentData);
    setIsGridSidebarOpen(true);
  };

  const closeSidebar = (shouldFetchDocuments: boolean) => {
    setIsSidebarOpen(false);
    setCurrentIndex(null);
    setCurrentLoadingTmsLocation(""); // Reset the new state
    if (shouldFetchDocuments) {
      // Reset loading states before fetching new documents
      setIsDocumentsReady(false);
      setIsPackingListReady(false);

      // Chiama subito fetchPackingListDocuments (non dipende da stops)
      fetchPackingListDocuments();

      // fetchDocuments aspetta che stops sia pronto
      if (stops && stops.length > 0) {
        fetchDocuments();
      }
    }
  };

  const closeGridSidebar = (shouldFetchDocuments: boolean) => {
    setIsGridSidebarOpen(false);
    setGridDocumentData(null);
    if (shouldFetchDocuments) {
      // Reset loading states before fetching new documents
      setIsDocumentsReady(false);
      setIsPackingListReady(false);

      // Chiama subito fetchPackingListDocuments (non dipende da stops)
      fetchPackingListDocuments();

      // fetchDocuments aspetta che stops sia pronto
      if (stops && stops.length > 0) {
        fetchDocuments();
      }
    }
  };

  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate(-1);
  };
  const { getDocumentDetails } = useTripDocument();
  const handleOpenFilePreview = async (documentId: string) => {
    try {
      const documentDetails = await getDocumentDetails(documentId);
      const pdfBlob = new Blob([documentDetails], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (error) {
      console.error(
        "Errore durante il recupero o la visualizzazione del documento:",
        error
      );
    }
  };
  const windowWidth = useWindowSize();
  const isMobile = useMemo(() => windowWidth < 700, [windowWidth]);
  // Computed loading state that considers all loading conditions
  const isLoading = useMemo(() => {
    return (
      isInitialLoading ||
      isDocumentLoading ||
      isUpdatingDocuments ||
      !isDocumentsReady ||
      !isPackingListReady ||
      isTermsLoading
    );
  }, [
    isInitialLoading,
    isDocumentLoading,
    isUpdatingDocuments,
    isDocumentsReady,
    isPackingListReady,
    isTermsLoading,
  ]);

  // Separate loading state for PKL that doesn't depend on POD loading
  const isPKLLoading = useMemo(() => {
    return (
      isInitialLoading ||
      isPackingListLoading ||
      !isPackingListReady ||
      isTermsLoading
    );
  }, [
    isInitialLoading,
    isPackingListLoading,
    isPackingListReady,
    isTermsLoading,
  ]);
  return (
    <ThemeProvider theme={theme}>
      <Box sx={documentPage.box}>
        <TripDocumentsHeader
          isMobile={isMobile}
          onGoBack={handleGoBack}
          tripId={tripDetails?.tmsObjectNumber}
          key={tripDetails?.tmsObjectNumber}
        />

        {!isMobile && (
          <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
        )}

        {viewMode === "cards" ? (
          <>
            {stops
              .filter((stop) =>
                stop.relatedActivities?.some(
                  (activity: any) => activity.activityType === "DELIVERY"
                )
              )
              .map((stop, index) => {
                if (
                  stop.relatedLoads
                    .map((x) => x.podSectionRequired)
                    .reduce((acc, load, _) => acc || load, false)
                ) {
                  return (
                    <>
                      <Accordion
                        defaultExpanded={true}
                        key={`POD-${index}`}
                        sx={documentPage.accordion}
                        elevation={0}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreRounded
                              color="primary"
                              fontSize="large"
                            />
                          }
                          sx={documentPage.summary}
                        >
                          <Typography
                            variant="h6"
                            sx={documentPage.textSummary}
                          >
                            POD - {stop.invPartyName || "-"}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={
                            isMobile
                              ? {
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }
                              : documentPage.accordionDetails
                          }
                        >
                          {isLoading ? (
                            <Box sx={documentPage.box4}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            (() => {
                              const relatedLoadsLength =
                                stop.relatedLoads.length; // Definiamo la costante qui dentro
                              return stop.relatedLoads.map(
                                (load, loadIndex) => (
                                  <RelatedLoadsCardsDocument
                                    key={`${stop.tmsLocation}-${loadIndex}`}
                                    index={loadIndex}
                                    relatedLoads={[load]}
                                    relatedLoadsLength={relatedLoadsLength} // Passiamo la lunghezza
                                    filteredDocuments={filteredDocuments}
                                    tmsLocationRelatedLoad={load.tmsLocation}
                                    tmsLocation={stop.tmsLocation}
                                    setHasFetchedDocuments={() => {}}
                                    stopInvPartyName={stop.invPartyName}
                                    openSidebar={(
                                      deliveryIndex,
                                      loadingTmsLocation,
                                      action
                                    ) =>
                                      openSidebar(
                                        deliveryIndex,
                                        loadingTmsLocation,
                                        action
                                      )
                                    }
                                    unloadIndex={index}
                                  />
                                )
                              );
                            })()
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: isMobile ? "center" : "flex-end",
                              width: "100%",
                              height: "40px",
                              marginTop: "32px",
                            }}
                          >
                            {(() => {
                              // Check if ALL loading points for this delivery have valid documents (not default/empty ones)
                              const allLoadsHaveValidDocuments =
                                stop.relatedLoads.every((load) => {
                                  const relatedDocs = filteredDocuments.filter(
                                    (doc) => {
                                      const docLocation = doc.TESTO246_136;
                                      return (
                                        docLocation ===
                                        load.tmsLocation +
                                          "_" +
                                          stop.tmsLocation
                                      );
                                    }
                                  );

                                  // Consider only documents with DOCNUMBER > 0 (real documents, not default ones)
                                  const validDocs = relatedDocs.filter(
                                    (doc) => doc.DOCNUMBER && doc.DOCNUMBER > 0
                                  );
                                  const documentStatus = relatedDocs[0]?.STATO;

                                  const cardStatus =
                                    documentStatus === "Profilo Parziale" ||
                                    documentStatus === "In Verifica" ||
                                    documentStatus === "In Gestione" ||
                                    documentStatus === "Rejected"
                                      ? "to be evaluated"
                                      : documentStatus === "Gestito" ||
                                        documentStatus === "Verificata" ||
                                        documentStatus === "Approvato"
                                      ? "accepted"
                                      : documentStatus === "Annullato" ||
                                        documentStatus === "Errore"
                                      ? "rejected"
                                      : "to be uploaded";

                                  return (
                                    validDocs.length > 0 &&
                                    cardStatus !== "rejected"
                                  );
                                });

                              return (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => openSidebar(index, "", false)}
                                  disabled={
                                    allLoadsHaveValidDocuments || isLoading
                                  }
                                  sx={{ fontWeight: "bold" }}
                                  endIcon={<AddRounded fontSize="large" />}
                                >
                                  {t("screens.trips.tripDocuments.addDocument")}
                                </Button>
                              );
                            })()}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                      <Divider sx={{ width: "100%", marginTop: "8px" }} />
                    </>
                  );
                } else return <Box></Box>;
              })}
            {isPKLLoading ? (
              <Box sx={documentPage.box4}>
                <CircularProgress />
              </Box>
            ) : (
              <Accordion
                defaultExpanded={true}
                sx={documentPage.accordion}
                elevation={0}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreRounded color="primary" fontSize="large" />
                  }
                  sx={documentPage.summary}
                >
                  <Typography variant="h6" sx={documentPage.textSummary}>
                    {t("screens.trips.tripDocuments.additionalAttachments")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={
                    isMobile
                      ? {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          rowGap: "16px",
                        }
                      : {
                          ...documentPage.accordionDetails,
                          flexWrap: "wrap",
                          flexBasis: 50,
                        }
                  }
                >
                  {packingListDocuments.map((doc, index) => {
                    return (
                      <Card
                        key={`packing-${index}`}
                        elevation={1}
                        sx={{
                          borderWidth: "1px",
                          width: "360px",
                          height: "300px",
                          padding: "24px 16px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}
                        >
                          {doc?.DOCNAME}
                        </Typography>
                        <Fab
                          color="primary"
                          sx={cardDocument.iconButton2}
                          onClick={() =>
                            handleOpenFilePreview(String(doc?.DOCNUMBER || ""))
                          }
                        >
                          <DownloadSharp sx={{ color: "white" }} />
                        </Fab>
                        <Box sx={cardDocument.box1}>
                          <Box sx={cardDocument.box2}>
                            <Typography sx={loadsCard.loadingPoint}>
                              {t("screens.trips.tripDocuments.uploaded")}
                            </Typography>
                            <Typography sx={cardDocument.dateText}>
                              {doc?.CREATION_DATE
                                ? new Date(doc.CREATION_DATE).toLocaleString(
                                    "it-IT",
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                          <Box sx={cardDocument.box2}>
                            <Typography sx={loadsCard.loadingPoint}>
                              {t("screens.trips.tripDocuments.by")}
                            </Typography>
                            <Typography sx={cardDocument.dateText}>
                              {doc?.TESTO223_131}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              backgroundColor: "white",
                              width: "100%",
                              justifyContent: "flex-end",
                              borderRadius: "4px",
                              marginTop: "8px",
                              columnGap: "16px",
                            }}
                          ></Box>
                        </Box>
                      </Card>
                    );
                  })}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => openSidebar(0, "", true)}
                    disabled={isPKLLoading}
                    sx={{
                      position: isMobile ? "" : "absolute",
                      top: isMobile ? "auto" : 73,
                      right: isMobile ? "auto" : 0,
                      fontWeight: "bold",
                      zIndex: 1,
                      marginTop: isMobile ? "8px" : "auto",
                    }}
                    endIcon={<AddRounded fontSize="large" />}
                  >
                    {t("screens.trips.tripDocuments.addDocument")}
                  </Button>
                </AccordionDetails>
              </Accordion>
            )}
            <Divider
              sx={{
                width: "100%",
                height: packingListDocuments.length > 0 ? "1px" : "40px",
                marginTop: "8px",
              }}
            />
            <Accordion
              defaultExpanded={true}
              sx={documentPage.accordion}
              elevation={0}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreRounded color="primary" fontSize="large" />
                }
                sx={documentPage.summary}
              >
                <Typography variant="h6" sx={documentPage.textSummary}>
                  SOP & {t("screens.trips.tripDocuments.generalConditions")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={documentPage.accordionDetails}>
                {isTermsLoading ? (
                  <Box sx={documentPage.box4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {terms?.sop?.documentBody && (
                      <Button
                        href={
                          createDownloadLink(
                            terms.sop.documentBody,
                            "SOP.pdf"
                          ) || "#"
                        }
                        download="SOP.pdf"
                        variant="text"
                        startIcon={
                          <img
                            src={documentsIcon}
                            alt="Documents Icon"
                            style={documentsCard.icon}
                          />
                        }
                        sx={documentsCard.button}
                        fullWidth={false}
                      >
                        SOP
                      </Button>
                    )}
                    {terms?.generalTerms?.documentBody && (
                      <Button
                        href={
                          createDownloadLink(
                            terms.generalTerms.documentBody,
                            "IT General Terms.pdf"
                          ) || "#"
                        }
                        download="IT General Terms.pdf"
                        variant="text"
                        startIcon={
                          <img
                            src={documentsIcon}
                            alt="Documents Icon"
                            style={documentsCard.icon}
                          />
                        }
                        onClick={() => {}}
                        sx={documentsCard.button}
                        fullWidth={false}
                      >
                        {t("screens.trips.documentsCard.generalConditions")}
                      </Button>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
            <Divider sx={{ width: "100%", marginTop: "8px" }} />
          </>
        ) : isLoading ? (
          <Box sx={documentPage.box4}>
            <CircularProgress />
          </Box>
        ) : (
          <AgGridDocuments
            documents={[...enrichedDocuments, ...enrichedPackingListDocuments]}
            stops={stops}
            openGridSidebar={openGridSidebar}
          />
        )}

        {currentIndex !== null && (
          <SidebarAddDocument
            key={`${currentIndex}-${currentLoadingTmsLocation}`}
            isOpen={isSidebarOpen}
            callbackCloseSidebar={closeSidebar}
            index={currentIndex}
            loadingTmsLocation={currentLoadingTmsLocation}
            action={action}
            filteredDocuments={filteredDocuments}
          />
        )}

        {gridDocumentData && (
          <SidebarAddDocumentGrid
            key={`grid-${gridDocumentData.loadingTmsLocation}-${gridDocumentData.unloadingTmsLocation}`}
            isOpen={isGridSidebarOpen}
            callbackCloseSidebar={closeGridSidebar}
            action={action}
            gridDocumentData={gridDocumentData}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TripDocuments;
